/**
 * Distribution Detector — Nhận diện dòng tiền Chốt lời / Phân phối
 * 4 mức: RUNG_LAC / CHOT_LOI / PHAN_PHOI / GAY_TREND
 * 8 tín hiệu cốt lõi + 4 pattern hành vi + scoring
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Snackbar, Alert,
} from '@mui/material';
import {
  Shield, Visibility, SellOutlined, RemoveCircleOutline,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { distributionAPI } from '../services/api';
import {
  HeaderSection, LeaderboardSection, MarketOverviewSection, SectorDetailDrawer,
  SectorRegimeSection, TickerDrawer, Tile, TilesSkeleton,
} from '../components/distribution-detector';
const DistributionDetector = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [market, setMarket] = useState(null);
  const [scanDate, setScanDate] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [lookupTicker, setLookupTicker] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [sortKey, setSortKey] = useState('total_dist_score');
  const [sortDir, setSortDir] = useState('desc');
  const [drawerTicker, setDrawerTicker] = useState(null);
  const [sectorRegime, setSectorRegime] = useState(null);
  const [sectorRegimeSummary, setSectorRegimeSummary] = useState(null);
  const [sectorDetail, setSectorDetail] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [leaderboard, summaryRes, marketRes, sectorRes] = await Promise.all([
        distributionAPI.getLeaderboard({ min_level: 1, top_n: 500 }),
        distributionAPI.getSummary(),
        distributionAPI.getMarket().catch(() => ({ data: null })),
        distributionAPI.getSectorRegime().catch(() => ({ data: null })),
      ]);
      setData(leaderboard.data.data || []);
      setScanDate(leaderboard.data.scan_date);
      setSummary(summaryRes.data);
      setMarket(marketRes.data);
      setSectorRegime(sectorRes.data?.data || sectorRes.data || null);
      setSectorRegimeSummary(sectorRes.data?.summary || null);
    } catch (err) {
      setError(err.response?.data?.detail || t('distribution.errorLoad'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Filtering & sorting ──
  const filtered = data
    .filter(r => filter === 'all' || r.dist_action === filter)
    .filter(r => !search || r.ticker?.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const av = a[sortKey] ?? 0, bv = b[sortKey] ?? 0;
      return sortDir === 'desc' ? bv - av : av - bv;
    });

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const handleLookupTicker = useCallback(async () => {
    const normalizedTicker = lookupTicker.trim().toUpperCase();
    if (!normalizedTicker) {
      setError(t('distribution.lookup.empty'));
      return;
    }

    setLookupLoading(true);
    try {
      const response = await distributionAPI.getTicker(normalizedTicker, 30);
      const history = response.data?.data || response.data || [];

      if (!Array.isArray(history) || history.length === 0) {
        setError(t('distribution.lookup.noData', { ticker: normalizedTicker }));
        return;
      }

      const existingRow = data.find((row) => row.ticker === normalizedTicker);
      setDrawerTicker(existingRow || history[0] || { ticker: normalizedTicker });
      setLookupTicker(normalizedTicker);
    } catch (err) {
      setError(t('distribution.lookup.failed', { ticker: normalizedTicker }));
    } finally {
      setLookupLoading(false);
    }
  }, [data, lookupTicker, t]);

  return (
    <Box sx={{ maxWidth: 1480, mx: 'auto' }}>
      <HeaderSection
        loading={loading}
        scanDate={scanDate}
        onRefresh={fetchData}
        lookupTicker={lookupTicker}
        onLookupTickerChange={setLookupTicker}
        onLookupSubmit={handleLookupTicker}
        lookupLoading={lookupLoading}
      />

      <MarketOverviewSection loading={loading} market={market} />

      <SectorRegimeSection
        loading={loading}
        sectorRegime={sectorRegime}
        sectorRegimeSummary={sectorRegimeSummary}
        onSelectSector={setSectorDetail}
      />

      {/* ── SUMMARY TILES ── */}
      {loading && !summary && <TilesSkeleton />}
      {summary && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 1.5 }}>
            <Tile label={t('distribution.levels.shaking')} value={summary.level_1_count || 0} color="#10b981" Icon={Shield} sub={t('distribution.tiles.shakingSub')} />
            <Tile label={t('distribution.levels.profitTaking')} value={summary.level_2_count || 0} color="#f59e0b" Icon={Visibility} sub={t('distribution.tiles.profitTakingSub')} />
            <Tile label={t('distribution.levels.distributing')} value={summary.level_3_count || 0} color="#f97316" Icon={RemoveCircleOutline} sub={t('distribution.tiles.distributingSub')} />
            <Tile label={t('distribution.levels.trendBreak')} value={summary.level_4_count || 0} color="#ef4444" Icon={SellOutlined} sub={t('distribution.tiles.trendBreakSub')} />
          </Box>
        </Box>
      )}

      <LeaderboardSection
        data={data}
        filtered={filtered}
        loading={loading}
        summary={summary}
        filter={filter}
        onFilterChange={setFilter}
        search={search}
        onSearchChange={setSearch}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={handleSort}
        onSelectTicker={setDrawerTicker}
      />

      {/* ── TICKER DRAWER ── */}
      <TickerDrawer
        open={!!drawerTicker}
        onClose={() => setDrawerTicker(null)}
        ticker={drawerTicker?.ticker || drawerTicker}
        companyName={drawerTicker?.company_name}
        tickerMeta={drawerTicker}
      />

      {/* ── SECTOR DETAIL DRAWER ── */}
      {sectorDetail && (
        <SectorDetailDrawer
          open={!!sectorDetail}
          onClose={() => setSectorDetail(null)}
          sector={sectorDetail}
          onOpenTicker={(member) => {
            setSectorDetail(null);
            setDrawerTicker(member);
          }}
        />
      )}

      {/* ── ERROR SNACKBAR ── */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Box>
  );
};

export default DistributionDetector;
